import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import {
  MAILCHIMP_AUDIENCE_ID,
  MAILCHIMP_FORM_ACTION,
  MAILCHIMP_FORM_ID,
  MAILCHIMP_HONEYPOT_FIELD,
  MAILCHIMP_SUBSCRIBE_VALUE,
  MAILCHIMP_USER_ID,
} from "../../Card/utils/constants";

type TInput = {
  email: string;
};

function validateEmail(email: string) {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

const STORAGE_KEY = "userSubscribeEmail";

const SubscribeCard = () => {
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<TInput>();

  const onSubmit: SubmitHandler<TInput> = async (data) => {
    clearErrors();

    if (!validateEmail(data.email)) {
      setError("email", {
        type: "manual",
        message: "Please enter a valid email address",
      });
      setTimeout(() => {
        clearErrors("email");
      }, 3000);
      return;
    }

    const storageEmail = localStorage.getItem(STORAGE_KEY);
    if (storageEmail === data.email) {
      setError("email", {
        type: "manual",
        message: "You are already subscribed.",
      });
      setTimeout(() => {
        clearErrors("email");
      }, 3000);
      return;
    }

    if (
      !MAILCHIMP_FORM_ACTION ||
      !MAILCHIMP_USER_ID ||
      !MAILCHIMP_AUDIENCE_ID
    ) {
      setError("email", {
        type: "manual",
        message: "Subscription service keys are missing.",
      });
      setTimeout(() => {
        clearErrors("email");
      }, 3000);
      return;
    }

    try {
      const formData = new FormData();

      formData.append("u", MAILCHIMP_USER_ID as string);
      formData.append("id", MAILCHIMP_AUDIENCE_ID as string);
      formData.append("f_id", MAILCHIMP_FORM_ID as string);
      formData.append("subscribe", MAILCHIMP_SUBSCRIBE_VALUE);

      formData.append("EMAIL", data.email);

      if (MAILCHIMP_HONEYPOT_FIELD) {
        formData.append(MAILCHIMP_HONEYPOT_FIELD, "");
      }

      await fetch(MAILCHIMP_FORM_ACTION as string, {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });

      localStorage.setItem(STORAGE_KEY, data.email);
      reset();
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      setError("email", {
        type: "manual",
        message: "Sorry something went wrong, try one more time please.",
      });
      setTimeout(() => clearErrors("email"), 3000);
    }
  };

  return (
    <article className="subscribe-card p-[1.714vw] max-lg:p-[6.4vw] w-full  bg-[#E9EBF3] rounded-28 shadow-custom">
      <div className="flex flex-col gap-[0.571vw] max-lg:gap-[2.13 max-lg:mb-[6.4vw] mb-[0.625vw] max-lg:mb-[2.4vw]">
        <h2 className="text-32 font-luxora-regular max-lg:text-[6.4vw] font-normal leading-[1.1] text-black">
          Subscribe the
          <br /> AGNT Newsletter
        </h2>
        <p className="text-[#00000099]  text-16 max-lg:text-[4.26vw] leading-[1.1]">
          Get quick insights, tips, and more directly{" "}
          <br className="hidden lg:block" /> in your browser
        </p>
      </div>
      <AnimatePresence mode="wait">
        {errors.email?.message && (
          <motion.p
            key="emailError"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="absolute text-red-600 text-[4vw] font-luxora-regular bottom-0 lg:text-16  font-luxora"
          >
            {errors.email?.message}
          </motion.p>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        {isSuccess && (
          <motion.p
            key="emailSuccess"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="absolute text-black text-[4vw] bottom-0 lg:text-16  font-luxora"
          >
            Thank you for your subscription!
          </motion.p>
        )}
      </AnimatePresence>
      <form
        onSubmit={handleSubmit(onSubmit)}
        action={MAILCHIMP_FORM_ACTION || undefined}
        method="post"
        className="relative z-20"
      >
        <label htmlFor="your-email" className="sr-only">
          Your email
        </label>
        <input
          {...register("email")}
          placeholder="Your Email"
          type="text"
          id="your-email"
          className="bg-white placeholder:text-[#00000040] max-lg:h-[12.8vw] text-black font-luxora-regular max-lg:pl-[4.26vw] text-16 max-lg:text-[4.26vw] leading-[1.1] pl-[1.14vw] rounded-2xl h-[3.428vw] w-full outline-none"
        />
        <div className="absolute right-[0.285vw] max-lg:right-[1.06vw] top-1/2 -translate-y-1/2">
          <button
            disabled={isSubmitting}
            type="submit"
            className={clsx(
              "shadow-custom subscribe-button font-luxora-regular cursor-pointer max-lg:py-[2.93vw] max-lg:px-[5.33vw] py-[0.785vw] px-[1.428vw] bg-[#FFFFFF1A] text-white  leading-[1.1] text-16 max-lg:text-[4.26vw]",
              isSubmitting && "!opacity-75"
            )}
          >
            {isSubmitting ? "Subscribing..." : "Subscribe"}
          </button>
        </div>
      </form>
    </article>
  );
};

export default SubscribeCard;
